import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Database, TrendingUp, MapPin, Calculator, BarChart3, Shield, Clock } from 'lucide-react';
import { LeaderboardAd, InContentAd, RectangleAd } from '@/components/shared/AdSense';
import AdFrame from '@/components/shared/AdFrame';
import SeoHead from '@/components/shared/SeoHead';

const Methodology = () => {
  return (
    <>
      <SeoHead
        title="Methodology - Wage Atlas | How We Calculate Salary Data & Career Insights"
        description="Learn how Wage Atlas calculates salary data, career trends, and labor market insights. Our methodology explained with data sources and analysis techniques."
        canonical="https://wageatlas.com/methodology/"
      />

      <div className="min-h-screen bg-background">
        <div className="container mx-auto px-4 py-8">
          <div className="max-w-6xl mx-auto">
            <div className="grid lg:grid-cols-4 gap-8">
              <div className="lg:col-span-3 space-y-8">
                {/* Header */}
                <div className="text-center space-y-4">
                  <div className="flex items-center justify-center gap-2 mb-4">
                    <BarChart3 className="h-8 w-8 text-primary" />
                    <h1 className="text-3xl font-bold">Methodology</h1>
                  </div>
                  <p className="text-lg text-muted-foreground">
                    Understanding how we calculate salary data, career trends, and provide labor market insights.
                  </p>
                  <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
                    <Clock className="h-4 w-4" />
                    <span>Last updated: June 2026</span>
                  </div>
                </div>

                {/* Leaderboard Ad */}
                <AdFrame className="mb-8">
                  <LeaderboardAd />
                </AdFrame>

                {/* Data Sources */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Database className="h-5 w-5 text-primary" />
                      Primary Data Sources
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    <div>
                      <h3 className="font-semibold mb-3">U.S. Bureau of Labor Statistics (BLS)</h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="p-4 bg-muted/30 rounded-lg">
                          <h4 className="font-medium mb-2">Occupational Employment & Wage Statistics (OEWS)</h4>
                          <p className="text-sm text-muted-foreground">
                            Annual surveys covering ~800 occupations across all U.S. states and metropolitan areas. 
                            Provides mean and median wages, employment levels, and percentile distributions.
                          </p>
                        </div>
                        <div className="p-4 bg-muted/30 rounded-lg">
                          <h4 className="font-medium mb-2">
                            <Link to="/what-is-soc-code" className="hover:text-primary transition-colors">
                              Standard Occupational Classification (SOC)
                            </Link>
                          </h4>
                          <p className="text-sm text-muted-foreground">
                            Federal classification system organizing occupations into hierarchical categories. 
                            We use SOC 2018 codes for consistent occupation identification. 
                            <Link to="/what-is-soc-code" className="text-primary hover:underline ml-1">
                              Learn more about SOC codes
                            </Link>.
                          </p>
                        </div>
                      </div>
                    </div>

                    <div>
                      <h3 className="font-semibold mb-3">Bureau of Economic Analysis (BEA)</h3>
                      <div className="p-4 bg-muted/30 rounded-lg">
                        <h4 className="font-medium mb-2">Regional Price Parities (RPP)</h4>
                        <p className="text-sm text-muted-foreground">
                          Cost-of-living adjustments by metropolitan area and state. Used to calculate "real wages" 
                          that account for purchasing power differences across locations.
                        </p>
                      </div>
                    </div>

                    <div>
                      <h3 className="font-semibold mb-3">Additional Sources</h3>
                      <ul className="space-y-2">
                        <li className="flex items-start gap-2">
                          <span className="w-2 h-2 bg-primary rounded-full mt-2 flex-shrink-0"></span>
                          <span className="text-muted-foreground">
                            <strong>BLS Education & Training Data:</strong> Required education levels and training pathways
                          </span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="w-2 h-2 bg-primary rounded-full mt-2 flex-shrink-0"></span>
                          <span className="text-muted-foreground">
                            <strong>Industry Classifications:</strong> NAICS codes for industry-specific analysis
                          </span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="w-2 h-2 bg-primary rounded-full mt-2 flex-shrink-0"></span>
                          <span className="text-muted-foreground">
                            <strong>Economic Indicators:</strong> Unemployment rates, GDP growth, and labor force statistics
                          </span>
                        </li>
                      </ul>
                    </div>
                    <div className="mt-4 pt-4 border-t">
                      <p className="text-sm text-muted-foreground">
                        For comprehensive documentation of all data sources, attribution, data coverage, and update schedules, 
                        see our <Link to="/data-sources" className="text-primary hover:underline font-medium">Data Sources page</Link>.
                      </p>
                    </div>
                  </CardContent>
                </Card>

                {/* In-Content Ad */}
                <InContentAd />

                {/* Salary Calculations */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Calculator className="h-5 w-5 text-primary" />
                      Salary Calculations & Adjustments
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    <div>
                      <h3 className="font-semibold mb-3">Base Wage Metrics</h3>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="p-4 bg-blue-50 dark:bg-blue-950/20 rounded-lg border border-blue-200 dark:border-blue-800">
                          <h4 className="font-medium mb-2 text-blue-800 dark:text-blue-200">Mean Salary</h4>
                          <p className="text-sm text-blue-700 dark:text-blue-300">
                            Average of all reported salaries. Can be skewed by high earners but represents total compensation pool.
                          </p>
                        </div>
                        <div className="p-4 bg-green-50 dark:bg-green-950/20 rounded-lg border border-green-200 dark:border-green-800">
                          <h4 className="font-medium mb-2 text-green-800 dark:text-green-200">Median Salary</h4>
                          <p className="text-sm text-green-700 dark:text-green-300">
                            Middle value when salaries are ordered. Less affected by outliers, represents "typical" worker.
                          </p>
                        </div>
                        <div className="p-4 bg-purple-50 dark:bg-purple-950/20 rounded-lg border border-purple-200 dark:border-purple-800">
                          <h4 className="font-medium mb-2 text-purple-800 dark:text-purple-200">Percentiles</h4>
                          <p className="text-sm text-purple-700 dark:text-purple-300">
                            10th, 25th, 75th, and 90th percentiles show salary distribution across experience levels.
                          </p>
                        </div>
                      </div>
                    </div>

                    <div>
                      <h3 className="font-semibold mb-3">Real Wage Adjustments</h3>
                      <p className="text-muted-foreground mb-4">
                        We calculate cost-of-living adjusted salaries using BEA Regional Price Parities:
                      </p>
                      <div className="bg-muted/30 p-4 rounded-lg">
                        <code className="text-sm">
                          Real Wage = Nominal Wage × (National RPP / Local RPP)
                        </code>
                        <p className="text-xs text-muted-foreground mt-2">
                          Example: $80,000 in San Francisco (RPP: 123.4) = $80,000 × (100 / 123.4) = $64,851 in purchasing power
                        </p>
                      </div>
                      <p className="text-sm text-muted-foreground mt-3">
                        For a plain-English explainer and the latest RPP index for every state, see{' '}
                        <Link to="/real-wages/" className="text-primary hover:underline">
                          Real Wages, Explained
                        </Link>.
                      </p>
                    </div>

                    <div>
                      <h3 className="font-semibold mb-3">Hourly Rate Calculations</h3>
                      <p className="text-muted-foreground mb-2">
                        For salaried positions, we estimate hourly rates using:
                      </p>
                      <ul className="list-disc list-inside space-y-1 text-muted-foreground ml-4">
                        <li>Standard 2,080 working hours per year (40 hours × 52 weeks)</li>
                        <li>Adjustments for vacation time and holidays where applicable</li>
                        <li>Separate calculations for part-time and contract positions</li>
                      </ul>
                    </div>
                  </CardContent>
                </Card>

                {/* How the OEWS survey works */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <TrendingUp className="h-5 w-5 text-primary" />
                      How the OEWS Survey Actually Works
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4 text-muted-foreground">
                    <p>
                      Understanding where the numbers come from helps you judge how much weight to put on them.
                      The Occupational Employment and Wage Statistics program is not a poll of workers — it is a
                      survey of <strong className="text-foreground">employers</strong>. Twice a year, state workforce
                      agencies working with the BLS send questionnaires to a sample of business establishments,
                      asking them to report how many people they employ in each occupation and what those
                      people are paid, by wage band.
                    </p>
                    <p>
                      Each published estimate combines <strong className="text-foreground">six semiannual survey panels
                      collected over three years</strong> — roughly 1.1 million establishments covering about 57% of
                      U.S. employment. Older panels are adjusted to current wage levels before being combined.
                      This rolling design is what makes estimates available for ~800 occupations in small
                      geographic areas, but it also means a published figure is best understood as a
                      three-year centered estimate, not a snapshot of last month.
                    </p>
                    <p>
                      Three consequences worth knowing:
                    </p>
                    <ul className="list-disc list-inside space-y-2 ml-2">
                      <li>
                        <strong className="text-foreground">Employer-reported wages exclude</strong> self-employment income,
                        most equity compensation, and overtime premiums — so top-end pay in equity-heavy fields
                        (tech, finance) is understated relative to total compensation.
                      </li>
                      <li>
                        <strong className="text-foreground">In fast-moving markets, new-hire offers can run ahead</strong> of
                        the survey, since the data reflects all incumbents, including people hired years ago.
                        Treat OEWS as a reliable floor and structure, and supplement with live postings for
                        the leading edge.
                      </li>
                      <li>
                        <strong className="text-foreground">Small occupation-area cells are suppressed or noisy.</strong> When
                        an occupation has very few workers in a state, the BLS either withholds the estimate
                        or publishes one with a wide error band. Where data is missing on Wage Atlas, this
                        suppression — not a site error — is usually the reason.
                      </li>
                    </ul>
                  </CardContent>
                </Card>

                {/* SOC bridging */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <MapPin className="h-5 w-5 text-primary" />
                      Connecting 28+ Years of Data: SOC Code Bridging
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4 text-muted-foreground">
                    <p>
                      Our trend charts reach back to the late 1990s, which creates a problem the casual reader
                      never sees: the federal government has changed its occupational classification system
                      multiple times along the way. The 2010 revision of the Standard Occupational
                      Classification split some occupations, merged others, and created new ones; the 2018
                      revision did the same again. "Software Developers," for example, was reorganized in 2018,
                      and several healthcare and education occupations were redefined.
                    </p>
                    <p>
                      To build continuous histories, we map older SOC 2010 codes onto the current SOC 2018
                      taxonomy using the official BLS crosswalk tables. Where an old occupation maps cleanly to
                      one new code, the series connects directly. Where an occupation was split or merged, a
                      perfectly clean join is impossible — we connect the dominant successor and treat the
                      transition years with caution. Practically, this means:
                    </p>
                    <ul className="list-disc list-inside space-y-2 ml-2">
                      <li>
                        Long-run trends for stable occupations (nurses, electricians, truck drivers) are highly
                        reliable across the full history.
                      </li>
                      <li>
                        Trends that span a reclassification boundary (notably 2009-2012 and 2018-2021) for
                        restructured occupations can show artificial jumps that reflect the definition change,
                        not the labor market. We flag the affected series where feasible.
                      </li>
                    </ul>
                    <p>
                      For a full explanation of how the classification system works, see our{' '}
                      <Link to="/what-is-soc-code" className="text-primary hover:underline">SOC code guide</Link>.
                    </p>
                  </CardContent>
                </Card>

                {/* Limitations */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Shield className="h-5 w-5 text-primary" />
                      Limitations: What This Data Cannot Tell You
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4 text-muted-foreground">
                    <p>
                      We would rather you trust the data for the right reasons than for the wrong ones. These
                      are the known limits of what Wage Atlas can show, stated plainly:
                    </p>
                    <ul className="list-disc list-inside space-y-2 ml-2">
                      <li>
                        <strong className="text-foreground">No experience breakdown.</strong> OEWS does not record tenure or
                        years of experience. Percentiles are a useful proxy — entry-level workers cluster near
                        the 10th-25th percentile — but they are a proxy, not a measurement.
                      </li>
                      <li>
                        <strong className="text-foreground">Wages only, not total compensation.</strong> Health benefits,
                        retirement matching, bonuses paid irregularly, and equity are out of scope. For some
                        occupations these exceed 30% of cash wages.
                      </li>
                      <li>
                        <strong className="text-foreground">No employer-level detail.</strong> The data describes occupations
                        in areas, never specific companies. A specific employer can sit anywhere in (or beyond)
                        the published distribution.
                      </li>
                      <li>
                        <strong className="text-foreground">Cost-of-living adjustments are averages.</strong> Regional Price
                        Parities reflect the average consumption basket; your personal exposure to housing
                        costs may be higher or lower. See the worked examples in our{' '}
                        <Link to="/insights/cost-of-living-vs-salary" className="text-primary hover:underline">
                          cost-of-living guide
                        </Link>.
                      </li>
                      <li>
                        <strong className="text-foreground">Projections are extrapolations.</strong> Where we show growth
                        projections, they extend structural trends and cannot anticipate shocks — recessions,
                        pandemics, or rapid technology shifts.
                      </li>
                    </ul>
                    <p>
                      Our editorial guides — on{' '}
                      <Link to="/insights/how-to-read-salary-percentiles" className="text-primary hover:underline">reading percentiles</Link>,{' '}
                      <Link to="/insights/understanding-wage-trends" className="text-primary hover:underline">interpreting trends</Link>, and{' '}
                      <Link to="/insights/salary-negotiation-guide" className="text-primary hover:underline">using the data in negotiations</Link>{' '}
                      — are written specifically to help you work within these limits.
                    </p>
                  </CardContent>
                </Card>

                {/* Contact */}
                <Card>
                  <CardHeader>
                    <CardTitle>Questions & Feedback</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground mb-4">
                      We're committed to transparency in our analysis. If you have questions about our methodology 
                      or suggestions for improvement, please reach out.
                    </p>
                    <div className="space-y-2">
                      <p><strong>Research Team:</strong> research@wageatlas.com</p>
                      <p><strong>Data Questions:</strong> data@wageatlas.com</p>
                      <p><strong>Technical Issues:</strong> support@wageatlas.com</p>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Sidebar */}
              <div className="hidden lg:block space-y-8">
                <RectangleAd className="sticky top-4" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Methodology;